import React, { useEffect, useState, useCallback, useMemo, type ReactNode } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Header from '../Header/Header';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import {
  useApiClientsOrNull,
  Loader,
  BUILT_IN_ORG_ID,
  ContextFlowCanvasOrganism,
  ContextLevelInspectorOrganism,
  ContextRootInspectorMolecule,
  ContextCoverCardMolecule,
  ApplicabilityInspectorMolecule,
  ContextFooterSettingsMolecule,
  ContextProvenanceBannerMolecule,
  ContextViewsTabOrganism,
  MessageAtom,
  NotificationBadgeAtom,
  ConfirmDialogAtom,
  confirmDialogAtom,
  toContextDefinition,
  fromContextDefinition,
  deriveApplicability,
  buildApplicability,
  buildFilterPropertyOptions,
  buildFilterPerspectiveOptions,
  buildFilterRelationshipOptions,
  useAvailableTypes,
  useOrgAvailableTypes,
  useArcTemplates,
  resolveLevelEligibleSchemas,
  useContextPermissions,
  useUnifiedFilterCatalog,
  AspectCatalogMolecule,
  allowedLanesForSurface,
} from 'design-system/my-lib';
import type {
  ContextFlowLevel,
  ContextFlowRoot,
  ContextCoverDef,
  ContextFooterDef,
  SchemaForFilters,
  FilterState,
} from 'design-system/my-lib';
import type { ContextDefinition } from 'types/context';
import type { FilterGroupLeaf } from 'types/filters';
import type { AppDispatch, RootState } from '@center-pwa/store/store';
import {
  fetchContext,
  createContextThunk,
  updateContextThunk,
  cloneContextThunk,
  selectCurrentContext,
  selectContextsLoading,
  clearCurrentContext,
} from '@center-pwa/reducers/contextsReducer';
import './ContextEditorPage.scss';

/** Monotonic counter — guarantees unique level IDs even within the same millisecond. */
let levelIdCounter = 0;
const nextLevelId = (): string =>
  `lvl_${Date.now().toString(36)}_${(++levelIdCounter).toString(36)}`;

const EMPTY_APPLICABILITY_FILTER: FilterState = { propertyFilters: [], perspectiveFilters: [] };

const DEFAULT_ROOT: ContextFlowRoot = { rootType: 'sphere' };
const DEFAULT_COVER: ContextCoverDef = { name: '', description: '' };
const DEFAULT_FOOTER: ContextFooterDef = {
  defaultLimit: 50,
  maxDepth: 5,
  permissionCheck: 'view',
  includeAncestors: false,
};

/**
 * Handed to `renderTopbarExtras` so the host can drive Preview (`executeContextDefinition`)
 * against the page's current in-memory draft without the page owning the picker/panel UI.
 */
export interface ContextEditorTopbarExtrasContext {
  /** Levels present in the draft — Preview is disabled when this is empty. */
  canPreview: boolean;
  /** Builds the current unsaved `ContextDefinition` on demand (not memoized — call at click time). */
  buildDefinition: () => ContextDefinition;
}

/**
 * EAINT-862 Phase 3: an agent-proposed context walk, shown as a dashed ghost overlay on the
 * canvas until the user explicitly accepts (replacing the draft) or dismisses it. Same shape as
 * the composer-agent-service `create_context` tool's proposal (`definition` is the same
 * `{ root, levels, settings }` the editor itself saves).
 */
export interface ContextEditorGhostProposal {
  name: string;
  description?: string;
  applicability?: Record<string, unknown>;
  definition: ContextDefinition;
  useCaseTags?: string[];
}

interface ContextEditorPageProps {
  /** Renders as an embedded inline panel (no app Header, inline header bar). */
  inline?: boolean;
  /** Context system name when rendered inline — bypasses useParams. */
  inlineSystemName?: string;
  /** Called when "← View settings" back button is clicked in inline mode. */
  onBack?: () => void;
  /** Called when ✕ close button is clicked in inline mode. */
  onClose?: () => void;
  /**
   * Mobile alias (EAINT-862): hides every mutating affordance (name edit, level add/
   * edit/remove, cover/footer/applicability edits, Save) while keeping the "Save as
   * copy" clone escape hatch. A UX guard only — the server's own `edit` permission
   * check (`useContextPermissions`) and `isBuiltIn` gating are the real authz boundary.
   */
  readOnly?: boolean;
  /**
   * Host owns post-create navigation when provided — replaces the hard
   * `navigate('/contexts/:sn/edit')` so the Studio host can route to `/studio/context/:sn`
   * instead. Falls back to the legacy navigate when absent (non-Studio consumers).
   */
  onSaved?: (systemName: string, isNew: boolean) => void;
  /** Fires whenever `isDirty` changes — lets the host reflect dirty state in its own chrome. */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Wraps the existing inspector `<aside>` in the host's tab strip (Inspect/Agent/Distribution).
   * `isBuiltIn`/`isNew` are handed through because the host — not this page — owns whether the
   * Distribution tab is offered at all (EAINT-862 Phase 4: an unsaved draft or a built-in system
   * context has no distribution to show; the tab is simply absent, never a disabled placeholder).
   */
  renderSidePanel?: (inspector: ReactNode, ctx: { isBuiltIn: boolean; isNew: boolean }) => ReactNode;
  /**
   * Renders host-owned topbar controls (sphere picker opener + Preview, EAINT-862 Phase 2)
   * after the Walk/Views toggle. The host owns the picker/preview UI and API call; this page
   * only supplies the current unsaved definition and whether there's anything to preview.
   */
  renderTopbarExtras?: (ctx: ContextEditorTopbarExtrasContext) => ReactNode;
  /**
   * Deep-link level selection (Studio Desk finding rows / recent-template `?node=`) —
   * selects that level in the Walk inspector once the editor's levels have loaded.
   */
  initialSelectedLevelId?: string;
  /**
   * EAINT-862 Phase 3: an agent-proposed context walk, rendered as a ghost overlay on the canvas.
   * Absent → no overlay. The host (Studio Agent tab) owns the proposal's lifecycle; this page only
   * applies it to its own draft state on Accept.
   */
  ghost?: ContextEditorGhostProposal;
  /** Fires after the ghost has been applied to the draft (state already updated) — the host clears its own ghost state and fires analytics. */
  onAcceptGhost?: () => void;
  /** Fires when the user dismisses the ghost without applying it — the draft is left untouched. */
  onDismissGhost?: () => void;
}

const ContextEditorPage: React.FC<ContextEditorPageProps> = ({
  inline = false,
  inlineSystemName,
  onBack,
  onClose,
  readOnly = false,
  onSaved,
  onDirtyChange,
  renderSidePanel,
  renderTopbarExtras,
  initialSelectedLevelId,
  ghost,
  onAcceptGhost,
  onDismissGhost,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { systemName: routeSystemName } = useParams<{ systemName?: string }>();
  const systemName = inline ? inlineSystemName : routeSystemName;
  const logAnalyticsEvent = useAnalytics();

  const apiClients = useApiClientsOrNull();
  const currentContext = useSelector(selectCurrentContext);
  const contextLoading = useSelector(selectContextsLoading);
  const organizationId = useSelector(
    (state: RootState) => state.userProfile?.userSphere?.properties?.sphereId
  );
  // Metasphere navbar (matches ContextListPage) — the editor is a full page and
  // must keep the app chrome, not swallow it.
  const defaultLocale = process.env.REACT_APP_I18N_LOCALE || 'en-US';
  const userLocale = useSelector((s: RootState) => s.userProfile?.locale ?? defaultLocale);
  const userId = useSelector((s: RootState) => s.userProfile?.userId);
  const currentSphere = useSelector((s: RootState) => s.userProfile?.userSphere);

  // Schema types loaded on demand (lazy-schema-types / ADR-026) — replaces the
  // removed big-bang `state.schemas.schemas` selector. Sphere-scoped: feeds the
  // level/segment property, perspective, and relationship catalogs, which are
  // legitimately hierarchy-scoped (spec org-available-types.md § Out of Scope).
  const { schemas: allSchemas } = useAvailableTypes(apiClients, organizationId);

  // Org-scoped: the caller's SpiceDB-viewable types ∪ platform/system types,
  // independent of sphere hierarchy. Feeds only the C1 applicability
  // (Specific-types) picker — a sphere with no type-providing deployments must
  // not leave that picker empty (EAINT-730).
  const {
    schemas: orgAvailableSchemas,
    sources: orgAvailableSources,
    approachSources: orgApproachSources,
  } = useOrgAvailableTypes(apiClients);

  // Unscoped catalog — used only by the cover card's applicability picker, which
  // sets the *root* scope and therefore must offer every type.
  const availableProperties = useMemo(
    () => buildFilterPropertyOptions({ schemas: allSchemas as unknown as SchemaForFilters[], t, groupBy: 'category' }),
    [allSchemas, t]
  );
  const availablePerspectives = useMemo(
    () => buildFilterPerspectiveOptions({ schemas: allSchemas as unknown as SchemaForFilters[], t }),
    [allSchemas, t]
  );
  const availableRelationships = useMemo(
    () => buildFilterRelationshipOptions({ schemas: allSchemas as unknown as SchemaForFilters[], t }),
    [allSchemas, t]
  );
  // Arc templates are org-scoped and permission-checked server-side, so the lane's
  // catalog is fetched once per editor rather than derived from the schema set.
  // An empty list (no templates, or a failed fetch) hides the Arcs section.
  const { templates: arcTemplates } = useArcTemplates();

  // Same rule `filterPropertyBuilder` uses to gate the Arc lane: a schema
  // declares an arc via `flow`.
  const hasArcSchemas = useMemo(
    () => (allSchemas as unknown as SchemaForFilters[]).some((s) => Boolean((s as { flow?: unknown }).flow)),
    [allSchemas]
  );

  // The real six-lane "Add condition" catalog (EAINT-908 Phase 5 — wiring
  // `useUnifiedFilterCatalog` into a real host, against this page's own
  // `apiClients`/schemas, rather than a component-local fetch). No lane
  // restriction here (`context` is an unrestricted authoring surface); the
  // applicability picker below has its own withheld-lanes catalog.
  const unifiedFilterCatalog = useUnifiedFilterCatalog({
    apiClients,
    schemas: allSchemas as unknown as SchemaForFilters[],
    t,
    hasArcSchemas,
  });

  const renderFilterCatalog = useCallback(
    ({ onPick, onClose }: { onPick: (leaf: FilterGroupLeaf) => void; onClose: () => void }) => (
      <AspectCatalogMolecule
        entries={unifiedFilterCatalog.entries}
        recent={unifiedFilterCatalog.recent}
        loading={unifiedFilterCatalog.loading}
        error={unifiedFilterCatalog.measuresError}
        onRetry={unifiedFilterCatalog.retryMeasures}
        onPick={(entry) => onPick(unifiedFilterCatalog.pickEntry(entry))}
        onCancel={onClose}
      />
    ),
    [unifiedFilterCatalog]
  );

  // The applicability picker's own catalog (OQ-1 / Gap B): `validateApplicability` is
  // synchronous and can never evaluate a perspective/relationship/arc/measure leaf (SC13),
  // so its "Add condition" catalog offers only the aspect + identity lanes
  // `allowedLanesForSurface('applicability').catalogLanes` names — the same predicate that
  // gates `ApplicabilityEditorMolecule`'s legacy `allowedLanes`, not a second decision.
  const applicabilityCatalogLanes = allowedLanesForSurface('applicability').catalogLanes;
  const applicabilityCatalogEntries = useMemo(
    () => unifiedFilterCatalog.entries.filter((e) => applicabilityCatalogLanes?.includes(e.lane)),
    [unifiedFilterCatalog.entries, applicabilityCatalogLanes]
  );
  const applicabilityRecentEntries = useMemo(
    () => unifiedFilterCatalog.recent.filter((e) => applicabilityCatalogLanes?.includes(e.lane)),
    [unifiedFilterCatalog.recent, applicabilityCatalogLanes]
  );
  const renderApplicabilityCatalog = useCallback(
    ({ onPick, onClose }: { onPick: (leaf: FilterGroupLeaf) => void; onClose: () => void }) => (
      <AspectCatalogMolecule
        entries={applicabilityCatalogEntries}
        recent={applicabilityRecentEntries}
        loading={unifiedFilterCatalog.loading}
        error={unifiedFilterCatalog.measuresError}
        onRetry={unifiedFilterCatalog.retryMeasures}
        onPick={(entry) => onPick(unifiedFilterCatalog.pickEntry(entry))}
        onCancel={onClose}
      />
    ),
    [applicabilityCatalogEntries, applicabilityRecentEntries, unifiedFilterCatalog]
  );

  const isNew = !systemName;
  const mode = isNew ? 'new' : 'edit';
  const isBuiltIn = !!currentContext && currentContext.organization_id === BUILT_IN_ORG_ID;

  // Server-side `edit` decision (fail-closed) for the persisted context — a new, unsaved
  // draft has nothing to check permissions against yet, so it's excluded from the query
  // (SC1/permissions Rule 1: the client never re-derives authz, it only relays the server's).
  const { canEdit: serverCanEdit } = useContextPermissions(
    apiClients,
    !isNew ? systemName ?? null : null
  );

  const entryPath = (location.state as { entryPath?: string } | null)?.entryPath ?? 'direct';

  const [contextName, setContextName] = useState('');
  const [levels, setLevels] = useState<ContextFlowLevel[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [root, setRoot] = useState<ContextFlowRoot>(DEFAULT_ROOT);
  const [cover, setCover] = useState<ContextCoverDef>(DEFAULT_COVER);
  const [footer, setFooter] = useState<ContextFooterDef>(DEFAULT_FOOTER);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'walk' | 'views'>('walk');
  const [viewCount, setViewCount] = useState(0);

  // Fire analytics on mount
  useEffect(() => {
    logAnalyticsEvent('context_editor_opened', undefined, { mode });
  }, []);  // intentional: fire once on mount only

  // Warn on hard navigation away (tab close, refresh, external link) while there
  // are unsaved edits. In-app navigation is guarded separately by the back/close
  // handler; the HashRouter isn't a data router, so `useBlocker` isn't available.
  useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  // Load context for edit mode
  useEffect(() => {
    if (isNew || !apiClients?.schemaClient || !systemName) return;
    dispatch(fetchContext({ apiClient: apiClients.schemaClient, systemName }));
    return () => { dispatch(clearCurrentContext()); };
  }, [systemName, apiClients?.schemaClient]);

  // Populate editor state from the loaded context (name + deserialized definition)
  useEffect(() => {
    if (!currentContext || isNew) return;
    if (currentContext.system_name !== systemName) return;
    setContextName(currentContext.name?.default ?? '');
    setCover({
      name: currentContext.name?.default ?? '',
      description: currentContext.description?.default ?? '',
      useCaseTags: Array.isArray(currentContext.use_case_tags)
        ? currentContext.use_case_tags
        : [],
      ...deriveApplicability(currentContext.applicability),
    });
    if (currentContext.definition) {
      const { root: loadedRoot, levels: loadedLevels, footer: loadedFooter } =
        fromContextDefinition(currentContext.definition);
      setRoot(loadedRoot);
      setLevels(loadedLevels);
      setFooter(loadedFooter);
    }
    setIsDirty(false);
  }, [currentContext, systemName, isNew]);

  // Deep-link level selection (Studio Desk finding rows / recent-template `?node=`) — select
  // once the levels the deep link points at have actually loaded, not before.
  const [nodeParamConsumed, setNodeParamConsumed] = useState(false);
  useEffect(() => {
    if (!initialSelectedLevelId || nodeParamConsumed) return;
    if (!levels.some((level) => level.id === initialSelectedLevelId)) return;
    setSelectedId(initialSelectedLevelId);
    setNodeParamConsumed(true);
  }, [initialSelectedLevelId, nodeParamConsumed, levels]);

  // Lets the host reflect dirty state in its own chrome (e.g. an unsaved-changes indicator
  // in the Studio tab strip) without owning `isDirty` itself.
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const handleAddLevel = useCallback((direction: 'outwards' | 'inwards') => {
    const newLevel: ContextFlowLevel = {
      id: nextLevelId(),
      label: '',
      direction,
      repeat: 1,
      enrichment: {},
    };
    setLevels((prev) => [...prev, newLevel]);
    // Switch the rail to the newly-created level so the user lands on its
    // properties (e.g. picking a direction from the empty "Add your first
    // level" state should open that level's inspector, not stay on empty).
    setSelectedId(newLevel.id);
    setIsDirty(true);
  }, []);

  const handleLevelSelect = useCallback((id: string) => {
    setSelectedId((prev) => (prev === id ? null : id));
  }, []);

  const handleLevelChange = useCallback((updated: ContextFlowLevel) => {
    setLevels((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    setIsDirty(true);
  }, []);

  const handleRemoveLevel = useCallback((id: string) => {
    setLevels((prev) => prev.filter((l) => l.id !== id));
    setSelectedId((prev) => (prev === id ? null : prev));
    setIsDirty(true);
  }, []);

  const handleSelectApplicability = useCallback(() => handleLevelSelect('applicability'), [handleLevelSelect]);

  const handleApplicabilityModeChange = useCallback((applicabilityMode: ContextCoverDef['applicabilityMode']) => {
    setCover((prev) => ({ ...prev, applicabilityMode }));
    setIsDirty(true);
  }, []);

  const handleApplicabilityFamiliesChange = useCallback((applicabilityFamilies: string[]) => {
    setCover((prev) => ({ ...prev, applicabilityFamilies }));
    setIsDirty(true);
  }, []);

  const handleApplicabilityTypesChange = useCallback((applicabilityTypes: string[]) => {
    setCover((prev) => ({ ...prev, applicabilityTypes }));
    setIsDirty(true);
  }, []);

  const handleApplicabilityFilterChange = useCallback((applicabilityFilter: FilterState) => {
    setCover((prev) => ({ ...prev, applicabilityFilter }));
    setIsDirty(true);
  }, []);

  const handleDuplicateLevel = useCallback(
    (id: string) => {
      const src = levels.find((l) => l.id === id);
      if (!src) return;
      const clone: ContextFlowLevel = {
        ...src,
        id: nextLevelId(),
      };
      setLevels((prev) => {
        const idx = prev.findIndex((l) => l.id === id);
        const next = [...prev];
        next.splice(idx + 1, 0, clone);
        return next;
      });
      setIsDirty(true);
    },
    [levels]
  );

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setContextName(e.target.value);
    setIsDirty(true);
  }, []);

  const handleBackToLibrary = useCallback(() => {
    if (!isDirty) {
      navigate('/contexts');
      return;
    }
    confirmDialogAtom({
      header: t('context.editor.unsavedGuard.title'),
      message: t('context.editor.unsavedGuard.message'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: t('context.editor.unsavedGuard.leave'),
      rejectLabel: t('context.editor.unsavedGuard.stay'),
      variant: 'danger',
      accept: () => navigate('/contexts'),
    });
  }, [isDirty, navigate, t]);

  // Inline mode: back to view editor, close entirely.
  const handleInlineBack = useCallback(() => {
    if (!isDirty) { onBack?.(); return; }
    confirmDialogAtom({
      header: t('context.editor.unsavedGuard.title'),
      message: t('context.editor.unsavedGuard.message'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: t('context.editor.unsavedGuard.leave'),
      rejectLabel: t('context.editor.unsavedGuard.stay'),
      variant: 'danger',
      accept: () => onBack?.(),
    });
  }, [isDirty, onBack, t]);

  const handleInlineClose = useCallback(() => {
    if (!isDirty) { onClose?.(); return; }
    confirmDialogAtom({
      header: t('context.editor.unsavedGuard.title'),
      message: t('context.editor.unsavedGuard.message'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: t('context.editor.unsavedGuard.leave'),
      rejectLabel: t('context.editor.unsavedGuard.stay'),
      variant: 'danger',
      accept: () => onClose?.(),
    });
  }, [isDirty, onClose, t]);

  // Cover / footer / root edits must mark the context dirty too — the status
  // pill (and any future unsaved-changes guard) reads `isDirty`, and passing
  // the bare setters would leave those edits invisible to it.
  const handleCoverChange = useCallback((next: ContextCoverDef) => {
    setCover(next);
    setIsDirty(true);
  }, []);
  const handleFooterChange = useCallback((next: ContextFooterDef) => {
    setFooter(next);
    setIsDirty(true);
  }, []);
  const handleRootChange = useCallback((next: ContextFlowRoot) => {
    setRoot(next);
    setIsDirty(true);
  }, []);

  // Ghost walk (EAINT-862 Phase 3) — parsed once per proposal via the same serializer the editor
  // uses for load/save, so the ghost preview and an Accept both read the walk identically.
  const parsedGhost = useMemo(
    () => (ghost ? fromContextDefinition(ghost.definition) : null),
    [ghost]
  );

  const handleAcceptGhost = useCallback(() => {
    if (!ghost || !parsedGhost) return;
    setRoot(parsedGhost.root);
    setLevels(parsedGhost.levels);
    setFooter(parsedGhost.footer);
    setContextName(ghost.name);
    setCover((prev) => ({
      ...prev,
      name: ghost.name,
      description: ghost.description ?? '',
      useCaseTags: ghost.useCaseTags ?? prev.useCaseTags,
      ...deriveApplicability(ghost.applicability as Parameters<typeof deriveApplicability>[0]),
    }));
    setIsDirty(true);
    onAcceptGhost?.();
  }, [ghost, parsedGhost, onAcceptGhost]);

  const handleDismissGhost = useCallback(() => {
    onDismissGhost?.();
  }, [onDismissGhost]);

  const canSave = levels.length > 0;
  // The "Ready to save" badge appears only when there are unsaved changes AND the user can
  // actually save: not a read-only built-in, not the mobile `readOnly` alias, and — for a
  // persisted context — the server's own `edit` decision (fail-closed while it resolves).
  // A brand-new draft has no server decision to wait on, so it is not gated by it.
  const canEdit = !isBuiltIn && !readOnly && (isNew || serverCanEdit);
  // Escape hatch to a saved, editable copy — offered for built-ins (their own read-only
  // gating) and for the mobile alias (SC3: read-and-clone, no in-place edit anywhere).
  const canClone = !isNew && (isBuiltIn || readOnly);
  const readyToSave = canSave && isDirty && canEdit;

  // Save-state chip: ready to save > already-persisted context > unsaved draft.
  let statusIconClass = 'pi-circle';
  let statusLabel = t('context.editor.status.draft');
  if (readyToSave) {
    statusIconClass = 'pi-check-circle';
    statusLabel = t('context.editor.status.readyToSave');
  } else if (!isNew) {
    statusIconClass = 'pi-check';
    statusLabel = t('context.editor.status.saved');
  }

  const handleSave = useCallback(async () => {
    if (!apiClients?.schemaClient || !organizationId || !canSave || !canEdit) return;
    setIsSaving(true);
    try {
      const definition = toContextDefinition(root, levels, footer);
      const coverFields = {
        description: { default: cover.description },
        useCaseTags: cover.useCaseTags ?? [],
        applicability: buildApplicability(cover),
      };
      if (isNew) {
        const created = await dispatch(
          createContextThunk({
            apiClient: apiClients.schemaClient,
            payload: {
              name: { default: contextName || t('context.editor.namePlaceholder') },
              definition,
              ...coverFields,
            },
          })
        ).unwrap();
        logAnalyticsEvent('context_created', created.system_name, { entryPath });
        setIsDirty(false);
        if (onSaved) {
          onSaved(created.system_name, true);
        } else {
          navigate(`/contexts/${created.system_name}/edit`, { replace: true });
        }
      } else if (systemName) {
        await dispatch(
          updateContextThunk({
            apiClient: apiClients.schemaClient,
            systemName,
            payload: {
              name: { default: contextName },
              definition,
              ...coverFields,
            },
          })
        ).unwrap();
        setIsDirty(false);
        onSaved?.(systemName, false);
      }
    } finally {
      setIsSaving(false);
    }
  }, [apiClients, organizationId, canSave, canEdit, isNew, systemName, root, levels, footer, cover, contextName, dispatch, navigate, logAnalyticsEvent, entryPath, t, onSaved]);

  // "Save as copy to edit" — the primary action on the provenance banner and the
  // root lock banner (SC6). Clones the current (read-only) context and opens the
  // copy for editing; the clone is a brand-new org-owned context, so its own root
  // is locked again the moment it is saved by cloneContext.
  const handleCloneToEdit = useCallback(async () => {
    if (!apiClients?.schemaClient || !currentContext) return;
    setIsCloning(true);
    setCloneError(null);
    try {
      const cloned = await dispatch(
        cloneContextThunk({
          apiClient: apiClients.schemaClient,
          systemName: currentContext.system_name,
          payload: {
            newName: {
              default: t('context.editor.saveAsCopyNameTemplate', {
                name: currentContext.name?.default ?? '',
              }),
            },
          },
        })
      ).unwrap();
      logAnalyticsEvent('context_saved_as_copy', cloned.system_name, {
        sourceSystemName: currentContext.system_name,
        source: 'platform',
      });
      navigate(`/contexts/${cloned.system_name}/edit`);
    } catch (err: unknown) {
      // Cloning is the only way out of a read-only context — a silent failure
      // leaves the user with no path forward, so surface it beside the banner.
      const msg = err instanceof Error ? err.message : String(err);
      setCloneError(msg || t('context.editor.cloneError'));
    } finally {
      setIsCloning(false);
    }
  }, [apiClients, currentContext, dispatch, navigate, logAnalyticsEvent, t]);

  const isLoadingEdit = !isNew && !currentContext && contextLoading;
  const selectedLevel = levels.find((l) => l.id === selectedId) ?? null;
  const selectedLevelOrder = selectedLevel ? levels.indexOf(selectedLevel) + 1 : 0;

  // Root scope the level catalog can fall back through when no level/type filter
  // is inferable (SC1 hierarchy-reachable seed).
  const contextApplicability = useMemo(() => buildApplicability(cover), [cover]);

  // Per-level (and its segments') property catalog — scoped to the level's declared
  // type/label filter, else the hierarchy-reachable types, else all schemas (SC1, SC2).
  const selectedLevelEligibleSchemas = useMemo(
    () =>
      selectedLevel
        ? resolveLevelEligibleSchemas(
            selectedLevel,
            levels,
            contextApplicability,
            allSchemas as unknown as SchemaForFilters[]
          )
        : (allSchemas as unknown as SchemaForFilters[]),
    [selectedLevel, levels, contextApplicability, allSchemas]
  );
  const selectedLevelProperties = useMemo(
    () =>
      buildFilterPropertyOptions({
        // Sphere Type / Sphere Family options come from the org-wide set so
        // "Sphere Type" carries values and "Sphere Family" is emitted; custom
        // props stay scoped to the level's eligible schemas. Their value editor
        // is the SphereTypeSelect chip selector (FilterEditorPanelMolecule).
        schemas: orgAvailableSchemas as unknown as SchemaForFilters[],
        eligibleSchemas: selectedLevelEligibleSchemas,
        t,
        groupBy: 'category',
      }),
    [orgAvailableSchemas, selectedLevelEligibleSchemas, t]
  );

  // Unscoped catalog over every deployed org type, carrying `ownerTypes` on each
  // custom property. The advanced editor's rail tiers this live against the
  // filter's own type scope, and its search reaches across all of it so a
  // property on an out-of-scope type is still findable.
  const orgWideProperties = useMemo(
    () =>
      buildFilterPropertyOptions({
        schemas: orgAvailableSchemas as unknown as SchemaForFilters[],
        t,
        groupBy: 'category',
      }),
    [orgAvailableSchemas, t]
  );
  const selectedLevelScopeTypes = useMemo(
    () => selectedLevelEligibleSchemas.map((s) => s.system_name),
    [selectedLevelEligibleSchemas]
  );

  // Assignee-narrowing search props for the perspective lane (SC3). Mirrors
  // TreeMap's page-level pattern (TreeMapGrid.tsx): the editor authors a
  // definition, not a single sphere, so `sphereId` anchors to the context's
  // declared root sphere (if any) rather than a "current" sphere.
  const perspectiveSearchProps = useMemo(
    () => ({
      schemaSystemName: '',
      sphereId: root.sphereIds?.[0] ?? '',
      direction: 'both' as const,
      hops: 1,
    }),
    [root.sphereIds]
  );

  const levelInspectorPanel: React.ReactNode = selectedLevel ? (
    <ContextLevelInspectorOrganism
      level={selectedLevel}
      order={selectedLevelOrder}
      onChange={handleLevelChange}
      onDelete={handleRemoveLevel}
      onDuplicate={handleDuplicateLevel}
      readOnly={!canEdit}
      availableProperties={selectedLevelProperties}
      orgWideProperties={orgWideProperties}
      scopeTypes={selectedLevelScopeTypes}
      availablePerspectives={availablePerspectives}
      availableRelationships={availableRelationships}
      arcTemplates={arcTemplates}
      familyTypeSchemas={orgAvailableSchemas}
      perspectiveSearchProps={perspectiveSearchProps}
      renderCatalog={renderFilterCatalog}
    />
  ) : (
    <>
      <div className="context-editor-page__inspector-header">
        <span className="context-editor-page__inspector-icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4.037 4.688a.495.495 0 0 1 .651-.651l16 6.5a.5.5 0 0 1-.063.947l-6.124 1.58a2 2 0 0 0-1.438 1.435l-1.579 6.126a.5.5 0 0 1-.947.063z"></path>
          </svg>
        </span>
        <div>
          <div className="context-editor-page__inspector-eyebrow">
            {t('context.editor.inspector.eyebrow')}
          </div>
          <div className="context-editor-page__inspector-title">
            {t('context.editor.inspector.emptyTitle')}
          </div>
        </div>
      </div>
      <div className="context-editor-page__inspector-empty">
        <span className="context-editor-page__inspector-empty-icon" aria-hidden="true">
          <i className="pi pi-database" />
        </span>
        <p className="context-editor-page__inspector-empty-title">
          {levels.length > 0
            ? t('context.editor.inspector.selectLevelTitle')
            : t('context.editor.inspector.addFirstLevel')}
        </p>
        <p className="context-editor-page__inspector-empty-desc">
          {levels.length > 0
            ? t('context.editor.inspector.selectLevelDesc')
            : t('context.editor.inspector.addFirstLevelDesc')}
        </p>
        {levels.length === 0 && canEdit && (
          <div className="context-editor-page__direction-picks">
            {(
              [
                {
                  dir: 'outwards' as const,
                  icon: 'pi-arrow-right',
                  label: t('context.editor.inspector.direction.outwards'),
                  desc: t('context.editor.inspector.direction.outwardsDesc'),
                },
                {
                  dir: 'inwards' as const,
                  icon: 'pi-arrow-left',
                  label: t('context.editor.inspector.direction.inwards'),
                  desc: t('context.editor.inspector.direction.inwardsDesc'),
                },
              ] as const
            ).map((r) => (
              <button type="button"
                key={r.dir}
                className={`context-editor-page__direction-pick context-editor-page__direction-pick--${r.dir}`}
                onClick={() => handleAddLevel(r.dir)}
                aria-label={r.label}
              >
                <span className="context-editor-page__direction-pick-icon" aria-hidden="true">
                  <i className={`pi ${r.icon}`} />
                </span>
                <span className="context-editor-page__direction-pick-text">
                  <span className="context-editor-page__direction-pick-label">{r.label}</span>
                  <span className="context-editor-page__direction-pick-desc">{r.desc}</span>
                </span>
                <i className="pi pi-arrow-right context-editor-page__direction-pick-arrow" aria-hidden="true" />
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );

  const applicabilityInspectorPanel: React.ReactNode = selectedId === 'applicability' ? (
    <ApplicabilityInspectorMolecule
      mode={cover.applicabilityMode ?? 'any'}
      families={cover.applicabilityFamilies ?? []}
      types={cover.applicabilityTypes ?? []}
      filter={cover.applicabilityFilter ?? EMPTY_APPLICABILITY_FILTER}
      onModeChange={handleApplicabilityModeChange}
      onFamiliesChange={handleApplicabilityFamiliesChange}
      onTypesChange={handleApplicabilityTypesChange}
      onFilterChange={handleApplicabilityFilterChange}
      availableProperties={availableProperties}
      availablePerspectives={availablePerspectives}
      availableRelationships={availableRelationships}
      arcTemplates={arcTemplates}
      familyTypeSchemas={orgAvailableSchemas}
      sources={orgAvailableSources}
      approachSources={orgApproachSources}
      perspectiveSearchProps={perspectiveSearchProps}
      readOnly={!canEdit}
      renderCatalog={renderApplicabilityCatalog}
    />
  ) : levelInspectorPanel;

  return (
    <>
      <ConfirmDialogAtom />
      {!inline && <Header sphereId={userId ?? ''} defaultLanguage={userLocale} sphere={currentSphere} />}
      <div
        className={`context-editor-page${inline ? ' context-editor-page--inline' : ''}`}
        data-testid="context-editor-page"
      >
      {/* Top bar */}
      <header className="context-editor-page__top-bar">
        <button type="button"
          className="context-editor-page__back-btn"
          onClick={inline ? handleInlineBack : handleBackToLibrary}
          aria-label={inline ? t('context.editor.backToViewSettings', 'View settings') : t('context.editor.backToLibrary')}
        >
          <i className="pi pi-arrow-left" aria-hidden="true" />
          <span>{inline ? t('context.editor.backToViewSettings', 'View settings') : t('context.editor.backToLibrary')}</span>
        </button>
        <span className="context-editor-page__top-bar-sep" aria-hidden="true" />

        <span className="context-editor-page__icon" aria-hidden="true">
          <i className="pi pi-database" />
        </span>

        <div className="context-editor-page__name-group">
          {inline && (
            <span className="context-editor-page__name-prefix">
              {t('context.editor.contextPrefix', 'Context')}
              {' · '}
            </span>
          )}
          <input
            className="context-editor-page__name-input"
            value={contextName}
            onChange={handleNameChange}
            placeholder={t('context.editor.namePlaceholder')}
            aria-label={t('context.editor.namePlaceholder')}
            disabled={!canEdit}
          />
          {isNew && (
            <span className="context-editor-page__name-hint">
              {t('context.editor.nameHint')}
            </span>
          )}
        </div>

        <span className="context-editor-page__spacer" />

        {/* Walk / Views toggle */}
        <div className="context-editor-page__walk-views-wrapper">
          <div className="context-editor-page__walk-views-control">
            <button type="button"
              className={`context-editor-page__walk-views-btn ${
                activeTab === 'walk' ? 'context-editor-page__walk-views-btn--active' : ''
              }`}
              onClick={() => !isNew && setActiveTab('walk')}
              aria-label={t('context.editor.toggle.walk', 'Walk')}
              aria-pressed={activeTab === 'walk'}
              disabled={isNew}
            >
              <i className="pi pi-sitemap" aria-hidden="true" />
              <span>{t('context.editor.toggle.walk', 'Walk')}</span>
            </button>
            <button type="button"
              className={`context-editor-page__walk-views-btn ${
                activeTab === 'views' ? 'context-editor-page__walk-views-btn--active' : ''
              }`}
              onClick={() => !isNew && setActiveTab('views')}
              aria-label={t('context.editor.toggle.views', 'Views')}
              aria-pressed={activeTab === 'views'}
              disabled={isNew}
              data-testid="views-toggle-btn"
            >
              <i className="pi pi-list" aria-hidden="true" />
              <span>{t('context.editor.toggle.views', 'Views')}</span>
            </button>
          </div>
          {/* In inline mode show badge always (not just when views tab active) */}
          {!isNew && (inline ? viewCount > 0 : activeTab === 'views') && (
            <NotificationBadgeAtom count={viewCount} severity="info" className="context-editor-page__views-badge" />
          )}
          {isNew && (
            <span className="context-editor-page__views-hint" title={t('context.editor.views.saveContextFirst')}>
              {t('context.editor.views.saveContextFirst')}
            </span>
          )}
        </div>

        {/* Host-owned topbar controls — sphere picker opener + Preview (EAINT-862 Phase 2). */}
        {renderTopbarExtras?.({
          canPreview: levels.length > 0,
          buildDefinition: () => toContextDefinition(root, levels, footer),
        })}

        {/* Dirty / saved pill — only for editable contexts; "Ready to save" only
            when there are unsaved changes the user can actually save. */}
        {canEdit && (
          <span
            className={`context-editor-page__status-pill ${readyToSave ? 'context-editor-page__status-pill--ready' : ''}`}
            data-testid="context-editor-status"
          >
            <i
              className={`pi ${statusIconClass}`}
              aria-hidden="true"
            />
            {statusLabel}
          </span>
        )}

        {/* A read-only context (built-in, mobile alias, or no server `edit` grant) can't be
            saved in place — its primary action is to branch a copy, so it takes the primary
            slot instead of a dead/absent Save (SC3: no Save affordance at all when the user
            cannot edit and cannot clone). */}
        {canClone ? (
          <button type="button"
            className="context-editor-page__save-btn Save-button"
            disabled={isCloning}
            onClick={handleCloneToEdit}
            aria-busy={isCloning}
            aria-label={t('context.editor.builtInCloneCta')}
            data-testid="context-editor-save-as-copy"
          >
            <i className={`pi ${isCloning ? 'pi-spin pi-spinner' : 'pi-copy'}`} aria-hidden="true" />
            {t('context.editor.builtInCloneCta')}
          </button>
        ) : canEdit ? (
          <button type="button"
            className="context-editor-page__save-btn Save-button"
            disabled={!canSave || isSaving}
            onClick={handleSave}
            aria-label={t('context.editor.actions.save')}
            data-testid="context-editor-save"
          >
            <i className="pi pi-check" aria-hidden="true" />
            {t('context.editor.actions.save')}
          </button>
        ) : null}

        {/* Close button — inline mode only */}
        {inline && (
          <button
            type="button"
            className="context-editor-page__close-btn"
            onClick={handleInlineClose}
            aria-label={t('context.editor.close', 'Close')}
          >
            <i className="pi pi-times" aria-hidden="true" />
          </button>
        )}
      </header>

      {/* Read-only provenance banner for built-in / platform contexts (SC6) */}
      {isBuiltIn && (
        <div className="context-editor-page__readonly-banner" data-testid="context-readonly-banner">
          <ContextProvenanceBannerMolecule
            source="platform"
            message={t('context.editor.readOnlyBanner')}
          />
          {cloneError && (
            <MessageAtom
              severity="error"
              text={cloneError}
              data-testid="context-clone-error"
            />
          )}
        </div>
      )}

      {/* Body: Views tab or walk canvas + inspector */}
      {activeTab === 'views' && systemName ? (
        <div className="context-editor-page__body context-editor-page__body--views">
          <ContextViewsTabOrganism
            contextSystemName={systemName}
            contextDisplayName={contextName}
            readOnly={!canEdit}
            onCountChange={setViewCount}
            onAnalyticsEvent={(event, meta) => logAnalyticsEvent(event, undefined, meta)}
          />
        </div>
      ) : (
      <div className="context-editor-page__body">
        {/* Main canvas */}
        <div className="context-editor-page__canvas">
          {isLoadingEdit ? (
            <div className="context-editor-page__loading">
              <Loader />
            </div>
          ) : (
            <>
              {/* Cover card — applicability (column B) is a selectable summary that
                  opens the rail (SC3); editing itself happens in ApplicabilityInspectorMolecule. */}
              <ContextCoverCardMolecule
                cover={cover}
                onChange={handleCoverChange}
                applicabilitySelected={selectedId === 'applicability'}
                onSelectApplicability={handleSelectApplicability}
                readOnly={!canEdit}
              />

              {/* Flow canvas */}
              <ContextFlowCanvasOrganism
                levels={levels}
                selectedId={selectedId}
                onLevelSelect={handleLevelSelect}
                onAddLevel={handleAddLevel}
                onRemoveLevel={handleRemoveLevel}
                onReorderLevels={setLevels}
                readOnly={!canEdit}
                ghostLevels={parsedGhost?.levels}
                onAcceptGhost={handleAcceptGhost}
                onDismissGhost={handleDismissGhost}
              />

              {/* Empty-state CTA — shown below canvas when no levels */}
              {levels.length === 0 && canEdit && (
                <div className="context-editor-page__empty-ctas">
                  <button type="button"
                    className="context-editor-page__add-level-cta"
                    onClick={() => handleAddLevel('outwards')}
                    data-testid="context-editor-add-level-cta"
                    aria-label={t('context.editor.canvas.addLevel')}
                  >
                    <span className="context-editor-page__cta-icon">
                      <i className="pi pi-plus" aria-hidden="true" />
                    </span>
                    <span className="context-editor-page__cta-text">
                      <span className="context-editor-page__cta-title">
                        {t('context.editor.canvas.addLevel')}
                      </span>
                      <span className="context-editor-page__cta-hint">
                        {t('context.editor.canvas.addLevelHint')}
                      </span>
                    </span>
                  </button>
                </div>
              )}

              {levels.length === 0 && canEdit && (
                <div className="context-editor-page__empty-hint" role="note">
                  <i className="pi pi-info-circle" aria-hidden="true" />
                  <span>{t('context.editor.canvas.emptyHint')}</span>
                </div>
              )}

              {/* Footer settings */}
              <ContextFooterSettingsMolecule
                settings={footer}
                onChange={handleFooterChange}
                readOnly={!canEdit}
              />
            </>
          )}
        </div>

        {/* Inspector — the host wraps this in its own tab strip (Inspect/Agent/Distribution)
            via `renderSidePanel`; standalone (non-Studio) consumers render it bare. */}
        {(() => {
          const inspectorAside = (
            <aside className="context-editor-page__inspector" aria-label={t('context.editor.inspector.eyebrow')}>
              {selectedId === 'root' ? (
                <ContextRootInspectorMolecule
                  root={root}
                  onChange={handleRootChange}
                  readOnly={!canEdit || !isNew}
                  onCloneClick={!isNew ? handleCloneToEdit : undefined}
                />
              ) : applicabilityInspectorPanel}
            </aside>
          );
          return renderSidePanel
            ? renderSidePanel(inspectorAside, { isBuiltIn, isNew })
            : inspectorAside;
        })()}
      </div>
      )}
      </div>
    </>
  );
};

export default ContextEditorPage;
